import { Component, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Editor, Toolbar } from 'ngx-editor';
import { map, Observable } from 'rxjs';
import { MealPeriod, Venue, VenueHours } from 'src/app/models/venue.model';
import { VenueService } from 'src/app/services/aws/venue/venue.services';
import { canComponentExit } from 'src/app/services/guard/un-saved-changes.guard';
import { NavMenuService } from 'src/app/services/nav-menu/nav-menu.service';
import { EditorHelperService } from 'src/app/services/shared/richText.service';
import { ConfirmDialogComponent } from 'src/app/widgets/confirm-dialog/confirm-dialog.component';


@Component({
    selector: 'app-venue-detail',
    templateUrl: './venue-detail.component.html',
    styleUrls: ['./venue-detail.component.scss'],
    standalone: false
})
export class VenueDetailComponent implements OnInit, canComponentExit, OnDestroy {
  public showGlobalSpinner: boolean = false;
  venue: Venue | null = null;
  editingVenue: Venue | null = null;
  editMode: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  hasChanges: boolean = false;
  hasBasicInfoChanges: boolean = false;
  hasHoursChanges: boolean = false;
  editedFields: Set<string> = new Set();
  editedHours: Map<string, Set<string>> = new Map();
  originalValues: Map<string, any> = new Map();

  editor: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic', 'underline'], // Text styling
    ['ordered_list', 'bullet_list'], // Lists
    ['align_left', 'align_center', 'align_right', 'align_justify'], // Text alignment
    ['text_color', 'background_color'], // Color text
    ['link'], // Links
  ];

  horizontalPosition: MatSnackBarHorizontalPosition = 'right';
  verticalPosition: MatSnackBarVerticalPosition = 'top';

  breadcrumbOptions = {
    breadcrumb: [
      {
        label: 'Dashboard',
        itemclass: 'Breadcrumb-item',
        linkclass: 'Breadcrumb-link',
        link: '/admin/dashboard'
      },
      {
        label: 'Manage Venues',
        itemclass: 'Breadcrumb-item',
        linkclass: 'Breadcrumb-link',
        link: '/admin/settings/manageVenues'
      },
      {
        label: 'View/Edit Venue',
        itemclass: 'Breadcrumb-item',
        linkclass: 'Breadcrumb-link',
        link: `/admin/settings/manageVenues/${this.route.snapshot.paramMap.get('id')}`
      }
    ],
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private venueService: VenueService,
    private snackBar: MatSnackBar,
    private sanitizer: DomSanitizer,
    private navMenuService: NavMenuService,
    private dialog: MatDialog,
    private editorHelperService: EditorHelperService
  ) { }
  ngOnDestroy(): void {
    this.editor.destroy();
  }


  canLeaveComponent(nextState: string): boolean | Observable<boolean> {
    if (this.editMode) {
      return this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: "",
          body: 'Are you sure you want to exit?',
          buttonTextNo: "Keep Editing",
          buttonTextYes: "Exit"
        },
        panelClass: 'notification-submit-dialog',
        disableClose: true
      }).afterClosed()
        .pipe(map(choice => {
          if (choice) {
            return true;
          }
          else if (!choice && nextState === '/admin/programs-external') {
            this.navMenuService.isFacilitatorView();
            this.navMenuService.disableDropdownMenu$.next('admin');
          }
          return false;
        }));
    }
    else {
      return true;
    }
  };

  ngOnInit(): void {
    this.editor = this.editorHelperService.createEditor();
    this.loadVenueDetails();
  }

  loadVenueDetails(): void {
    const venueId = this.route.snapshot.paramMap.get('id');
    if (!venueId) {
      this.errorMessage = 'Venue ID is missing';
      return;
    }

    const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    this.showGlobalSpinner = true;
    this.venueService.getVenueById(venueId).subscribe({
      next: (response) => {

        const venues = response.venus;
        this.showGlobalSpinner = false;
        if (venues && venues.length > 0) {

          const venue = venues.find(v => v.venueId == venueId);
          if (venue) {
            const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
            venue.hours = venue.hours.sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));
            this.venue = venue;
          } else {
            this.venue = null;
          }
          if (!this.venue?.hours || this.venue.hours.length === 0) {
            this.initializeDefaultHours();
          }
          this.editingVenue = this.deepCopy(this.venue);
          if (!response?.venus[0]?.isComplete) {

            this.snackBar.open('Some of the details are not properly loaded, please try again later.', 'OK', {
              duration: 8000,
              horizontalPosition: this.horizontalPosition,
              verticalPosition: this.verticalPosition,
              panelClass: ['error-snackbar']
            });
          }
        }
        else {
          this.errorMessage = 'Venue not found';
          this.showGlobalSpinner = false;
        }
      },
      error: (error) => {
        this.showGlobalSpinner = false;
        this.errorMessage = 'Failed to load venue details. Please try again later.';

      }
    });
  }

  initializeDefaultHours(): void {
    if (!this.venue) return;

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    this.venue.hours = days.map(day => ({
      day,
      status: 0, // Closed by default
      mealPeriods: []
    }));
  }

  onStatusChange(dayHours: VenueHours): void {
    if (!this.venue || !this.editingVenue) return;

    // Convert status to number to ensure correct comparison
    const status = Number(dayHours.status);

    // Find the original day status
    const originalDay = this.venue.hours?.find(h => h.day === dayHours.day);
    if (originalDay && originalDay.status !== status) {
      // Track the edited status for this day
      this.trackHoursEdit(dayHours.day, 'status');
    }

    // When changing status to default (1), create a single default meal period
    if (status === 2) {
      // Clear any existing meal periods and add just the default one
      dayHours.mealPeriods = [{
        mealId: 0,
        mealName: 'DEFAULT',
        isClosed: false,
        openTime: '09:00',
        closeTime: '17:00'
      }];
    }

    // When changing status to meal periods (2), create breakfast, lunch, dinner meal periods
    if (status === 3) {
      // Clear any existing meal periods and add the meal-specific ones
      dayHours.mealPeriods = [
        {
          mealId: 1,
          mealName: 'Breakfast',
          isClosed: false,
          openTime: '07:00',
          closeTime: '10:00'
        },
        {
          mealId: 2,
          mealName: 'Lunch',
          isClosed: false,
          openTime: '11:30',
          closeTime: '14:00'
        },
        {
          mealId: 3,
          mealName: 'Dinner',
          isClosed: false,
          openTime: '17:00',
          closeTime: '21:00'
        }
      ];
    }

    // When closed (0), clear meal periods
    if (status === 0 || status === 1) {
      dayHours.mealPeriods = [];
    }

    this.checkForChanges();
  }

  toggleEditMode(): void {
    this.editMode = true;
    this.editingVenue = this.deepCopy(this.venue);
    this.editedFields.clear();
    this.editedHours.clear();
    this.originalValues.clear();

    // Store original values for comparison
    if (this.venue) {
      this.originalValues.set('venueName', this.venue.venueName);
      this.originalValues.set('location', this.venue.location);
      this.originalValues.set('shortDesc', this.venue.shortDesc);
      this.originalValues.set('longDesc', this.venue.longDesc);
      this.originalValues.set('links', this.venue.links);
    }
  }

  getSanitizedHtml(html: string): SafeHtml {
    // return this.sanitizer.sanitize(SecurityContext.HTML, html);
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  
  cancelEdit(): void {
    this.editMode = false;
    this.editingVenue = this.deepCopy(this.venue);
    this.hasChanges = false;
    this.hasBasicInfoChanges = false;
    this.hasHoursChanges = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.editedFields.clear();
    this.editedHours.clear();
    this.originalValues.clear();
  }

  saveVenue(): void {
    if (!this.editingVenue) return;

    this.successMessage = '';
    this.errorMessage = '';

    // Validate venue data before saving
    if (!this.validateVenue()) {
      return;
    }
    this.showGlobalSpinner = true;

    this.venueService.updateVenue(this.editingVenue).subscribe({
      next: () => {
        this.showGlobalSpinner = false;
        this.successMessage = 'Venue details saved successfully';
        this.snackBar.open('Venue details saved successfully.', 'OK', {
          duration: 5000,
          horizontalPosition: this.horizontalPosition,
          verticalPosition: this.verticalPosition,
          panelClass: ['success-snackbar']
        });
        this.venue = this.deepCopy(this.editingVenue);
        this.editMode = false;
        this.hasChanges = false;
        this.hasBasicInfoChanges = false;
        this.hasHoursChanges = false;
        this.editedFields.clear();
        this.editedHours.clear();
        this.originalValues.clear();

        this.loadVenueDetails()
        setTimeout(() => this.successMessage = '', 3000);
      },
      error: (error) => {
        this.showGlobalSpinner = false;
        this.errorMessage = 'Failed to save venue details. Please try again.';

      }
    });
  }

  validateVenue(): boolean {
    if (!this.editingVenue) return false;

    // Basic validation
    if (!this.editingVenue.venueName?.trim()) {
      this.errorMessage = 'Venue name is required';
      return false;
    }

    // Hours validation
    if (this.editingVenue.hours) {
      for (const dayHours of this.editingVenue.hours) {
        if (dayHours.status === 1 || dayHours.status === 2) {
          for (const mealPeriod of dayHours.mealPeriods) {
            if (!mealPeriod.isClosed) {
              if (!mealPeriod.openTime || !mealPeriod.closeTime) {
                this.errorMessage = `Please enter both open and close times for ${mealPeriod.mealName} on ${dayHours.day}`;
                return false;
              }
            }
          }
        }
      }
    }

    return true;
  }

  navigateBack(): void {
    this.router.navigate(['/admin/settings/manageVenues']);
  }

  // Helper method to format time for display
  formatTime(time: string): string {
    if (!time) return '';

    try {
      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;

      return `${formattedHour}:${minutes} ${ampm}`;
    } catch (e) {
      return time;
    }
  }

  // Deep copy function to create a clean copy of objects
  deepCopy<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  // Check for changes in the form
  checkForChanges(): void {
    if (!this.venue || !this.editingVenue) return;

    // Check for basic info changes
    this.hasBasicInfoChanges =
      this.isFieldEdited('venueName') ||
      this.isFieldEdited('location') ||
      this.isFieldEdited('shortDesc') ||
      this.isFieldEdited('longDesc') ||
      this.isFieldEdited('links');

    // Check for hours changes
    this.hasHoursChanges = this.editedHours.size > 0;

    // Overall changes
    this.hasChanges = this.hasBasicInfoChanges || this.hasHoursChanges;
  }

  // Update edited fields based on changes
  updateEditedFields(): void {
    if (!this.venue || !this.editingVenue) return;

    // Check basic info fields
    if (this.editingVenue.venueName !== this.venue.venueName) {
      this.editedFields.add('venueName');
    }

    if (this.editingVenue.location !== this.venue.location) {
      this.editedFields.add('location');
    }

    if (this.editingVenue.shortDesc !== this.venue.shortDesc) {
      this.editedFields.add('shortDesc');
    }

    if (this.editingVenue.longDesc !== this.venue.longDesc) {
      this.editedFields.add('longDesc');
    }

    // Check hours fields
    // This is handled separately by the onTimeChange and onStatusChange methods
  }

  // Check if a field has been edited
  isFieldEdited(fieldName: string): boolean {
    if (!this.venue || !this.editingVenue || !this.originalValues.has(fieldName)) return false;

    const originalValue = this.originalValues.get(fieldName);
    const currentValue = (this.editingVenue as any)[fieldName];

    return originalValue !== currentValue;
  }

  // Handle field changes
  onFieldChange(field: string): void {
    if (!this.originalValues.has(field)) {
      this.originalValues.set(field, this.venue![field as keyof Venue]);
    }
    this.hasChanges = true;
  }

  // Track hours edits
  trackHoursEdit(day: string, field: string): void {
    if (!this.editedHours.has(day)) {
      this.editedHours.set(day, new Set());
    }

    this.editedHours.get(day)?.add(field);
    this.checkForChanges();
  }

  // Check if a specific hours field has been edited
  isHoursFieldEdited(day: string, field: string): boolean {
    return this.editedHours.has(day) && this.editedHours.get(day)?.has(field) === true;
  }

  // Handle time changes for meal periods
  onTimeChange(day: string, mealPeriod: MealPeriod, field: string): void {
    if (!this.venue || !this.editingVenue) return;

    // Find the original meal period
    const originalDay = this.venue.hours?.find(h => h.day === day);
    if (!originalDay) return;

    const originalMeal = originalDay.mealPeriods.find(m => m.mealName === mealPeriod.mealName);

    // Track changes if values differ
    if (originalMeal && (originalMeal as any)[field] !== (mealPeriod as any)[field]) {
      // Create a unique identifier for this meal's time field
      const mealTimeKey = `${mealPeriod.mealName}_${field}`;

      // Track this edit
      this.trackHoursEdit(day, mealTimeKey);
    }
  }

  // Check if a meal time has been edited
  isMealTimeEdited(day: string, mealName: string, field: string): boolean {
    const mealTimeKey = `${mealName}_${field}`;
    return this.isHoursFieldEdited(day, mealTimeKey);
  }

  // Check if day status has been edited
  isDayStatusEdited(day: string): boolean {
    return this.isHoursFieldEdited(day, 'status');
  }

  // Helper to get status as a number
  getStatusAsNumber(dayHours: VenueHours): number {
    return typeof dayHours.status === 'number' ? dayHours.status : parseInt(dayHours.status as string, 10);
  }
  // Handle meal closed status changes
  onMealClosedChange(day: string, mealPeriod: MealPeriod): void {
    if (!this.venue || !this.editingVenue) return;

    // Find the original meal period
    const originalDay = this.venue.hours?.find(h => h.day === day);
    if (!originalDay) return;

    const originalMeal = originalDay.mealPeriods.find(m => m.mealName === mealPeriod.mealName);

    // Track changes if values differ
    if (originalMeal && originalMeal.isClosed !== mealPeriod.isClosed) {
      // Create a unique identifier for this meal's closed status
      const mealClosedKey = `${mealPeriod.mealName}_isClosed`;

      // Track this edit
      this.trackHoursEdit(day, mealClosedKey);
    }
  }

  // Check if meal closed status has been edited
  isMealClosedEdited(day: string, mealName: string): boolean {
    const mealClosedKey = `${mealName}_isClosed`;
    return this.isHoursFieldEdited(day, mealClosedKey);
  }

  // Method to update character count
  updateCharCount(): void {
    if (this.editingVenue && this.editingVenue.shortDesc && this.editingVenue.shortDesc.length > 500) {
      this.editingVenue.shortDesc = this.editingVenue.shortDesc.substring(0, 500);
    }
  }

}